import { Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { CacheItem } from '../interfaces/cache-item';

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private cache = new Map<string, CacheItem<any>>();

  // 💾 Store value with TTL
  set<T>(key: string, value: T, ttlMs: number = 60000): void {
    this.cache.set(key, {
      value: structuredClone(value),
      expires: Date.now() + ttlMs
    });
  }

  // 📥 Get value if not expired
  get<T>(key: string): T | null {
    const item = this.cache.get(key);

    if (!item) return null;

    // ❌ expired → remove
    if (Date.now() > item.expires) {
      this.cache.delete(key);
      return null;
    }

    return structuredClone(item.value);
  }

  // 🧹 clear all cache
  clear(): void {
    this.cache.clear();
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }

  // 🔥 Wrap HTTP call with caching
  wrap<T>(
    key: string,
    request$: Observable<T>,
    ttlMs: number = 60000
  ): Observable<T> {

    const cached = this.get<T>(key);

    if (cached) {
      return of(structuredClone(cached));
    }

    return request$.pipe(
      tap(response => this.set(key, structuredClone(response), ttlMs))
    );
  }
}
